/**
 * dataHelpers.js
 * Utilities for managing climate data and entities
 * Migration from front/src/helpers.js to ES6 architecture
 */
import { indicatorsConfiguration } from '../config/settings.js';

// ===== GENERAL UTILITIES =====

/**
 * Converts array to object with specified key
 * @param {Array} array - Array to convert
 * @param {string} key - Key to use for object
 * @param {*} value - Default value
 * @returns {Object} Converted object
 */
export const arrayToObject = (array = [], key = 'key', value = true) => {
  const result = {};
  for (const arrayItem of array) {
    result[arrayItem[key]] = value;
  }
  return result;
};

/**
 * Capitalizes first letter of text
 * @param {string} text - Text to transform
 * @returns {string} Text with capitalized first letter
 */
export const capitalizeFirstLetter = (text) => (text.length > 1
  ? text.charAt(0).toUpperCase() + text.slice(1)
  : text);

// ===== INDICATOR MANAGEMENT =====

/**
 * Gets entity statuses for indicator list
 * @param {Object} entity - Entity (commune/EPCI)
 * @param {Object} indicatorList - Indicator list
 * @returns {Object} Indicator statuses for entity
 */
const getEntityStatuses = (entity, indicatorList = indicatorsConfiguration) => {
  const entityIndicatorStatuses = {};
  Object.entries(indicatorList).forEach(([key, value]) => {
    entityIndicatorStatuses[key] = value?.preselect(entity);
  });
  return entityIndicatorStatuses;
};

/**
 * Gets indicators with their status for an entity
 * @param {Object} entity - Entity (commune/EPCI)
 * @param {Array} allIndicators - List of all indicators
 * @returns {Object} Indicators with their status
 */
export const getIndicatorsWithStatus = (entity, allIndicators = Object.keys(indicatorsConfiguration)) => {
  const entityStatuses = getEntityStatuses(entity);
  const entityIndicators = entity.indicateurs || [];

  // Filter indicators based on data presence and enable condition
  // preselect only controls whether they are checked, not whether they appear
  const indicators = allIndicators.filter(indicator => {
    // Exclude if no data exists for this indicator
    const hasData = entityIndicators.some(({ id }) => id === indicator);
    if (!hasData) return false;

    // Check if indicator has an `enable` function
    const config = indicatorsConfiguration[indicator];
    if (config?.enable && typeof config.enable === 'function') {
      return config.enable(entity);
    }

    return true;
  });

  // Convert to object with status
  const result = {};
  for (const indicator of indicators) {
    result[indicator] = entityStatuses[indicator];
  }
  return result;
};

/**
 * Gets data for a specific indicator
 * @param {Array} indicators - Indicator list
 * @param {string} indicatorId - Indicator ID
 * @returns {*} Indicator data
 */
export const getIndicatorData = (indicators = [], indicatorId = 'G1') =>
  indicators.find(({ id }) => id === indicatorId)?.data;

/**
 * Gets altitude text for a specific indicator
 * @param {Array} indicators - Indicator list
 * @param {string} indicatorId - Indicator ID
 * @returns {string|undefined} Altitude text
 */
export const getIndicatorAltiText = (indicators, indicatorId) =>
  indicators.find(({ id }) => id === indicatorId)?.alti_text;

/**
 * Selects appropriate season based on screen size
 * @param {Array} seasons - Available seasons list
 * @param {boolean} isSmallScreen - Small screen indicator
 * @returns {string} Selected season
 */
export const getSelectedSeason = (seasons, isSmallScreen) => {
  let selectedSeason = '';
  const hasSelectedSeason = seasons && isSmallScreen && seasons[0] !== 'annuel';
  const summerIndex = hasSelectedSeason ? seasons.indexOf('été') : -1;

  if (hasSelectedSeason && summerIndex !== -1) {
    selectedSeason = seasons[summerIndex];
  }
  if (hasSelectedSeason && summerIndex === -1) {
    [selectedSeason] = seasons;
  }

  return selectedSeason;
};

